/**
 * Tasks: ff-admin's take on Meta's internal Tasks tool.
 *
 * Left rail of saved searches and projects; a toolbar with New Task, search and
 * the List / Cards / Gantt / Overview tabs; the selected task opens in a panel
 * beside the view (full screen on phones). State lives in the URL:
 * /admin/tasks/:id?view=&search=&project=&q=&owner=&priority=&status=&sort=&done=
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Plus, RefreshCw, Save, Search, X } from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { Button } from '@/components/ui/button';
import SearchableSelect from '@/client/components/admin/SearchableSelect';
import {
  type Task,
  type TaskQuery,
  type TaskSort,
  useCreateSectionMutation,
  useCreateTaskMutation,
  useGithubSyncMutation,
  useSaveSearchMutation,
  useTaskMetaQuery,
  useTasksQuery,
} from '@/client/api/tasks/tasks';
import CardsView from './CardsView';
import GanttView from './GanttView';
import ListView from './ListView';
import OverviewView from './OverviewView';
import TaskPanel from './TaskPanel';
import TaskSidebar, { type SidebarSelection } from './TaskSidebar';
import { personOptions, PRIORITY, PRIORITY_ORDER, SELECT_CLASS, STATUS, STATUS_ORDER, type TaskViewProps } from './taskUi';

type View = 'list' | 'cards' | 'gantt' | 'overview';
const VIEWS: { key: View; label: string }[] = [
  { key: 'list', label: 'List' },
  { key: 'cards', label: 'Cards' },
  { key: 'gantt', label: 'Gantt' },
  { key: 'overview', label: 'Overview' },
];
const SORTS: { key: TaskSort; label: string }[] = [
  { key: 'priority', label: 'Priority' },
  { key: 'manual', label: 'Manual' },
  { key: 'updated', label: 'Updated' },
  { key: 'due', label: 'Target date' },
  { key: 'created', label: 'Newest' },
];

const TasksPage: React.FC = () => {
  useBreadcrumbs([{ label: 'Tasks', href: '/admin/tasks' }]);
  const { id: openId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const metaQ = useTaskMetaQuery();
  const meta = metaQ.data;
  const createTask = useCreateTaskMutation();
  const createSection = useCreateSectionMutation();
  const saveSearch = useSaveSearchMutation();
  const sync = useGithubSyncMutation();
  const [searchText, setSearchText] = useState(params.get('q') ?? '');
  const [naming, setNaming] = useState<'search' | 'section' | null>(null);
  const [nameValue, setNameValue] = useState('');

  const view = (params.get('view') as View) || 'list';
  const searchId = params.get('search') ?? (params.get('project') || params.get('q') ? '' : 'owner');
  const projectId = params.get('project') ?? '';
  const savedSearch = meta?.searches.find((s) => s.id === searchId);
  const project = meta?.projects.find((p) => p.id === projectId);
  const showDone = params.get('done') === '1';

  // The sidebar picks the base query; the toolbar narrows it.
  const query: TaskQuery = useMemo(() => {
    const base: TaskQuery = savedSearch ? { ...savedSearch.query } : projectId ? { project: projectId } : {};
    const owner = params.get('owner');
    const priority = params.get('priority');
    const status = params.get('status');
    const sort = params.get('sort') as TaskSort | null;
    const q = params.get('q');
    if (owner) base.owner = owner;
    if (priority) base.priority = priority;
    if (q) base.q = q;
    if (sort) base.sort = sort;
    else if (!base.sort) base.sort = projectId ? 'manual' : 'priority';
    if (status) base.status = status;
    else if (showDone) base.status = 'all';
    else if (!base.status) base.status = 'open';
    return base;
  }, [savedSearch, projectId, params, showDone]);

  const listQ = useTasksQuery(query);
  const tasks = listQ.data ?? [];

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v === null || v === '' ? next.delete(k) : next.set(k, v)));
    setParams(next, { replace: true });
  };
  const select = (s: SidebarSelection) => {
    setSearchText('');
    const next = new URLSearchParams();
    if (params.get('view')) next.set('view', params.get('view')!);
    if (s.search) next.set('search', s.search);
    if (s.project) next.set('project', s.project);
    navigate({ pathname: '/admin/tasks', search: next.toString() });
  };
  const open = (t: Pick<Task, 'id'> | 'new') =>
    navigate({ pathname: `/admin/tasks/${t === 'new' ? 'new' : t.id}`, search: params.toString() });
  const close = () => navigate({ pathname: '/admin/tasks', search: params.toString() });

  const onCreate: TaskViewProps['onCreate'] = async ({ title, sectionId, isMilestone }) => {
    await createTask.mutateAsync({
      title,
      projectId: project?.id ?? null,
      sectionId,
      isMilestone: !!isMilestone,
      ownerUid: savedSearch?.query.owner === 'me' ? meta?.me ?? null : null,
    });
  };

  const submitName = async () => {
    const name = nameValue.trim();
    if (name && naming === 'search') {
      const { sort: _sort, ...rest } = query;
      const created = await saveSearch.mutateAsync({ name, query: rest });
      select({ search: created.id });
    }
    if (name && naming === 'section' && project) await createSection.mutateAsync({ projectId: project.id, name });
    setNaming(null);
    setNameValue('');
  };

  const title = savedSearch?.name ?? project?.name ?? (query.q ? 'Search' : 'All tasks');
  const viewProps: TaskViewProps = {
    tasks,
    loading: listQ.isLoading,
    meta,
    project,
    query,
    selectedId: openId,
    onOpen: open,
    onCreate,
  };
  const panelOpen = !!openId;
  const filtersActive = ['owner', 'priority', 'status', 'q'].some((k) => params.get(k));

  return (
    <div className="flex h-[calc(100vh-4rem)] min-h-[32rem] overflow-hidden rounded-lg border border-gray-200 bg-white">
      <aside className="hidden w-60 shrink-0 border-r border-gray-200 md:block">
        <TaskSidebar
          meta={meta}
          selection={{ search: searchId || undefined, project: projectId || undefined }}
          onSelect={select}
          onSaveSearch={() => setNaming('search')}
        />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar: New Task, search, view tabs */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-3 py-2">
          <Button size="sm" onClick={() => open('new')}>
            <Plus className="mr-1 h-3.5 w-3.5" /> New Task
          </Button>
          <form
            className="flex min-w-[200px] flex-1 items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              update({ q: searchText.trim() || null });
            }}
          >
            <Search className="h-4 w-4 shrink-0 text-gray-400" />
            <input
              aria-label="Search tasks"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search for tasks, tags, #'s… (T123, #tag, owner:me, is:starred)"
              className="h-8 min-w-0 flex-1 border-0 text-sm outline-none placeholder:text-gray-400"
            />
            {params.get('q') && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  setSearchText('');
                  update({ q: null });
                }}
                className="text-gray-400 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </form>
          {filtersActive && (
            <span className="text-xs text-gray-500">{listQ.isLoading ? '…' : `${tasks.length} result${tasks.length === 1 ? '' : 's'}`}</span>
          )}
          {filtersActive && (
            <Button size="sm" variant="outline" onClick={() => setNaming('search')}>
              <Save className="mr-1 h-3.5 w-3.5" /> Save
            </Button>
          )}
          <div role="tablist" aria-label="View" className="ml-auto flex">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                role="tab"
                type="button"
                aria-selected={view === v.key}
                onClick={() => update({ view: v.key === 'list' ? null : v.key })}
                className={`border-b-2 px-3 py-1.5 text-sm ${view === v.key ? 'border-blue-600 font-semibold text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>

        {/* Project / search header with filters */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 bg-gray-50/60 px-3 py-2">
          <h1 className="text-base font-semibold text-gray-900">{title}</h1>
          {project && (
            <Button size="sm" variant="outline" className="h-7" onClick={() => setNaming('section')}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add Section
            </Button>
          )}
          {naming && (
            <form
              className="flex items-center gap-1"
              onSubmit={(e) => {
                e.preventDefault();
                void submitName();
              }}
            >
              <input
                autoFocus
                aria-label={naming === 'search' ? 'Saved search name' : 'Section name'}
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && setNaming(null)}
                placeholder={naming === 'search' ? 'Name this search' : 'Section name'}
                className="h-7 w-48 rounded border border-blue-300 px-2 text-sm outline-none"
              />
              <Button size="sm" className="h-7" type="submit">Add</Button>
              <Button size="sm" className="h-7" variant="ghost" type="button" onClick={() => setNaming(null)}>Cancel</Button>
            </form>
          )}
          {view !== 'overview' && (
            <div className="ml-auto flex flex-wrap items-center gap-2 text-xs text-gray-600">
              <label className="flex items-center gap-1">
                Sort
                <select aria-label="Sort" className={SELECT_CLASS} value={query.sort} onChange={(e) => update({ sort: e.target.value })}>
                  {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </label>
              <label className="flex items-center gap-1">
                Filter
                <SearchableSelect
                  className="w-48"
                  placeholder="Everyone"
                  value={params.get('owner') ?? ''}
                  onChange={(v) => update({ owner: v })}
                  options={personOptions(meta?.people, [
                    { value: '', label: 'Everyone' },
                    { value: 'me', label: 'Me' },
                    { value: 'none', label: 'Unowned' },
                  ])}
                />
              </label>
              <select aria-label="Priority filter" className={SELECT_CLASS} value={params.get('priority') ?? ''} onChange={(e) => update({ priority: e.target.value })}>
                <option value="">Priority</option>
                {PRIORITY_ORDER.map((p) => <option key={p} value={p}>{PRIORITY[p].label}</option>)}
              </select>
              <select aria-label="Status filter" className={SELECT_CLASS} value={params.get('status') ?? ''} onChange={(e) => update({ status: e.target.value })}>
                <option value="">Status</option>
                {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
              </select>
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={!showDone} onChange={(e) => update({ done: e.target.checked ? null : '1' })} />
                Hide Completed
              </label>
              <button
                type="button"
                title="Refresh linked GitHub issues; closes tasks whose issues are all closed"
                onClick={() => sync.mutate()}
                disabled={sync.isPending}
                className="flex items-center gap-1 rounded px-1.5 py-1 hover:bg-gray-200"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${sync.isPending ? 'animate-spin' : ''}`} /> GitHub
              </button>
            </div>
          )}
          {sync.data && (
            <span className="w-full text-xs text-gray-500">
              GitHub: checked {sync.data.issues} issue{sync.data.issues === 1 ? '' : 's'} on {sync.data.tasks} task{sync.data.tasks === 1 ? '' : 's'}; closed {sync.data.closed}.
              {sync.data.errors.length > 0 && <span className="text-amber-700"> {sync.data.errors.join('; ')}</span>}
            </span>
          )}
          {sync.isError && <span className="w-full text-xs text-red-600">{sync.error instanceof Error ? sync.error.message : 'GitHub sync failed'}</span>}
        </div>

        <div className="flex min-h-0 flex-1">
          <div className={`min-h-0 min-w-0 flex-1 overflow-auto ${panelOpen ? 'hidden lg:block' : ''}`}>
            {listQ.isError && view !== 'overview' ? (
              <p className="p-6 text-sm text-red-600">Could not load tasks.</p>
            ) : view === 'cards' ? (
              <CardsView {...viewProps} />
            ) : view === 'gantt' ? (
              <GanttView {...viewProps} />
            ) : view === 'overview' ? (
              <OverviewView {...viewProps} />
            ) : (
              <ListView {...viewProps} />
            )}
          </div>
          {panelOpen && (
            <section
              aria-label="Task detail"
              className="fixed inset-0 z-40 flex min-h-0 flex-col bg-white lg:static lg:z-auto lg:w-[460px] lg:shrink-0 lg:border-l lg:border-gray-200"
            >
              <TaskPanel
                key={openId}
                taskId={openId === 'new' ? 'new' : openId!}
                meta={meta}
                defaults={{ projectId: project?.id ?? null }}
                onClose={close}
                onCreated={(id) => navigate({ pathname: `/admin/tasks/${id}`, search: params.toString() })}
              />
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default TasksPage;
